import * as React from "react";
import {cs} from "../../../../../react/chain-services";
import {chain} from "../../../../../utils/fs";
import {Sorting} from "./sorting";
import {Pagination} from "./pagination";
import {DataTableD} from "./desktop/data-table-d";
import {GlobalResizeState} from "../../../../browser/global-resize-state";
import {DataTableM} from "./mobile/data-table-m";
import {cx} from "emotion";
import "./data-table-f.scss";
import {PageSize} from "../pagination-ui/page-size/page-size";
import {PageList} from "../pagination-ui/page-list/page-list";
import {UseState} from "../../../../../react/use-state";
import {cascadeChange} from "../../../../../utils/cascade";
import {EditActionInTable} from "../data-table-ui/edit-action-in-table/edit-action-in-table";
import {DeleteActionInTable} from "../data-table-ui/delete-action-in-table/delete-action-in-table";
import {keyed} from "../../../../../react/keyed";

/**
 * Flex/responsive DataTable with default support for pagination and sorting
 */
export const DataTableF = (
    {
        className=undefined, getDefaultSorting=undefined, paginationKey=undefined, darkRows="odd", columns, rows, controls, emptyAction, xdQaNamePrefix=undefined,
    }
) => cs(
    ["pageSize", (_, next) => UseState({
        initValue: 10,
        // initValue: 3,
        next,
    })],
    ["sorting", (_, next) => Sorting({
        getDefault: getDefaultSorting,
        next,
    })],
    ["sortedRows", ({sorting}, next) => next(rows && chain(
        rows,
        (_) => sorting.apply(_, (row, columnIndex) => chain(
            row.columns[columnIndex],
            (_) => _.sortValue ? _.sortValue() : _.text.toLowerCase(),
        )),
    ))],
    ["pagination", ({sorting, sortedRows, pageSize}, next) => Pagination({
        _key: JSON.stringify([paginationKey, sorting.value]),
        pageSize: pageSize.value,
        rows: sortedRows,
        next,
    })],
    ["displayedRows", ({pagination, sortedRows}, next) => next(pagination ? pagination.getDisplayedRows() : sortedRows)],

    ["viewSize", (_, next) => GlobalResizeState({
        translate: ({width}) => width < 1024 ? "md" : "lg",
        next,
    })],
    ({sorting, pagination, displayedRows, viewSize, pageSize}) => viewSize === "md" ? (
        DataTableM({
            rows: displayedRows,
            className: cx("data-table-f-5it mobile", className),
            darkRows,
            columns: columns.map((col, i) => ({
                ...col,
                className: cx(col.className, col.type ?? "text"),
            })),
            emptyAction,
            controls,
            pagination,
            extraTopHeight: 12,
        })
    ) : (
        DataTableD({
            rows: cascadeChange(displayedRows, "[*].columns[*]", (dc) => ({
                render: () => null,
                ...dc,
                ...dc.text ? {
                    render: () => dc.text,
                    sortValue: () => dc.text.toLowerCase(),
                } : dc.actions && {
                    render: () => dc.actions.map((action, i) => cs(
                        keyed(i),
                        () => action.type === "edit" ? (
                            EditActionInTable({
                                onClick: action.onClick,
                                xdQaName: action.xdQaName,
                            })
                        ) : (
                            DeleteActionInTable({
                                onDelete: action.onClick,
                                xdQaName: action.xdQaName,
                            })
                        ),
                    )),
                },
            })),
            className: cx("data-table-f-5it desktop", className),
            darkRows,
            columns: columns.map((col, i) => ({
                ...col,
                className: cx(col.className, col.type ?? "text"),
                sorting: !col.sortable ? undefined : {
                    direction: sorting.isSortingActiveForColumn({columnIndex: i}) && sorting.direction,
                    toggle: () => sorting.toggleColumn({
                        columnIndex: i,
                        defaultDirection: col.sortable.defaultDirection,
                    }),
                },
            })),
            emptyAction,

            getFooter: ({colCount}) => ({
                columns: [{
                    colSpan: colCount,
                    render: () => (
                        <div className="controls">
                            {rows?.length > 10 ? (
                                PageSize({state: pageSize, xdQaNamePrefix})
                            ) : (
                                <div className="spacer"/>
                            )}

                            {pagination.active && PageList({...pagination, xdQaNamePrefix})}

                            <div className="buttons">
                                {controls.map((control, i) => (
                                    <button {...{
                                        onClick: control.onClick,
                                        key: i,
                                        "xd-qa-type": "button",
                                        "xd-qa-name": control.xdQaName ? control.xdQaName : control.label,
                                    }}>
                                        {control.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ),
                }],
            }),
        })
    ),
);
